"use client";
import React from "react";
import { isDateInPast } from "@/lib/date";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import getCoursePrice from "@/lib/course/utils/getCoursePrice";
import { getCourseRunStartText } from "@/lib/course/utils/getCourseRunTilePrimaryText";
import { getCourseRunEndText } from "@/lib/course/utils/getCourseRunTitleSecondaryText";
import { CourseEnrollProps } from "@/components/CourseContent/EnrollButton/types";
import useCourseEnrollment from "../hooks/useCourseEnrollment";
import CourseEnrollButton from "../CourseEnrollButtons";
import AuditLink from "../CourseEnrollButtons/AuditLink";
import useTranslations from "@/i18n/useTranslations";

function CourseEnrollInline({
  buttonClass = "",
  className = "",
  courseRun,
  course,
  showEndDate = true,
  showMobileAuditLink = true,
  isStickyCta = false,
  name = "",
  location = "",
  showStartDate = true,
  showOnlyCta = false,
}: CourseEnrollProps) {
  const t = useTranslations("Course");
  const translate =
    t as unknown as import("@/lib/course/utils/getCourseInformation").CourseInfoTranslate;
  const {
    isUserEnrolledInRun,
    isCourseEnrollable,
    isPaidLeadLoading,
    isCourseArchived,
  } = useCourseEnrollment({
    courseRun,
    course,
  });
  if (isPaidLeadLoading) {
    return (
      <div className="flex flex-row gap-3 items-center bg-putty-100 p-4 rounded-xl border-putty-400 border">
        <Skeleton role="status" className="h-11 w-56 rounded-none" />
      </div>
    );
  }

  const enrollmentEnded = courseRun.enrollmentEnd
    ? isDateInPast(courseRun.enrollmentEnd)
    : false;

  const courseRunPrice = getCoursePrice(courseRun);
  const auditLinkClassName = isStickyCta ? "text-white max-md:p-0" : "";
  const fontColor = isStickyCta ? "text-white" : "";

  return (
    <>
      <div
        className={cn(
          "flex flex-col md:flex-row gap-4 sm:gap-2 items-center justify-between bg-putty-100 p-4 rounded-xl border-putty-400 border w-auto xl:w-[calc(50%-12px)] [&:only-child]:w-auto",
          className || "",
          showOnlyCta && "justify-center",
        )}
      >
        {!showOnlyCta && (
          <>
            {isCourseArchived || !isCourseEnrollable ? (
              <span className={`font-bold text-base ${fontColor}`}>
                {t("thisCourseIsArchived")}
              </span>
            ) : (
              <div className={`text-center md:text-left ${fontColor}`}>
                <div
                  className={`${showStartDate ? "block" : "hidden md:block"} font-bold ${!isStickyCta ? "md:mb-1" : ""}`}
                >
                  {getCourseRunStartText(courseRun, false, translate)}
                </div>
                {showEndDate ? (
                  <div className="md:mt-1">
                    {getCourseRunEndText(courseRun, translate)}
                  </div>
                ) : null}
              </div>
            )}
          </>
        )}

        {isCourseEnrollable ? (
          <div
            className={`flex flex-col md:flex-row gap-2 sm:gap-3 items-center ${isStickyCta ? "w-full md:w-auto md:pl-3" : ""} ${showOnlyCta ? "justify-center w-full" : ""}`}
          >
            <CourseEnrollButton
              buttonClass={buttonClass}
              courseRun={courseRun}
              course={course}
              name={name}
              location={location}
              price={courseRunPrice}
            />
            {!showOnlyCta && (
              <div
                className={`${showMobileAuditLink ? "block" : "hidden md:block"} `}
              >
                <AuditLink
                  courseRun={courseRun}
                  course={course}
                  className={auditLinkClassName}
                  name={name}
                  location={location}
                  variant={isStickyCta ? "anchor" : "button"}
                />
              </div>
            )}
          </div>
        ) : !isUserEnrolledInRun && enrollmentEnded ? (
          <span className={fontColor}>{t("futureDatesToBeAnnounced")}</span>
        ) : null}
      </div>
    </>
  );
}

export default CourseEnrollInline;
